import { useEffect, useRef, useState } from "react";
import { AccordionRoutesEnum } from "../../../../types/AccordionRoutes"
import { useStore } from "../../../../store/store";
import { adressevaelger, AdressevaelgerSelection } from "../../../../vendor/adressevaelger/adressevaelger.esm.js";
import "../../../../vendor/adressevaelger/adressevaelger.css";
import { ADRESSEVAELGER_TOKEN } from "../../../../constants/Adressevaelger";
import { Container, HintMessage, Input, InputErrorMessage, Label } from "./AdressInput.style";

export interface AddressData {
    addressText: string;
    address: string;
    houseNumber: string;
    floor: string;
    door: string;
    city: string;
    zipCode: string;
  }

interface Props {
    id: string, 
    name: string,
    labelText: string,
    placeholder: string,
    autocomplete: string,
    step: AccordionRoutesEnum,
    setSelectedAddress: (address: AddressData) => void;
}

const EMPTY_ADDRESS: AddressData = {
    addressText: "",
    address: "",
    houseNumber: "",
    floor: "",
    door: "",
    city: "",
    zipCode: "",
};

// "adresse" items nest the house number under `adresse.husnummer`; "husnummer" items have it at the top level.
const mapSelectionToAddressData = (selected: AdressevaelgerSelection, addressText: string): AddressData => {
    const husnummer = selected.adresse?.husnummer ?? selected.husnummer;

    return {
        addressText,
        address: husnummer?.vejnavn ?? "",
        houseNumber: husnummer?.husnummertekst ?? "",
        floor: selected.adresse?.etagebetegnelse ?? "",
        door: selected.adresse?.doerbetegnelse ?? "",
        zipCode: husnummer?.postnummer?.postnr ?? "",
        city: husnummer?.postnummer?.navn ?? "",
    };
};

export default function AdressInput({id, name, labelText, placeholder, autocomplete, step, setSelectedAddress} : Props) {
    const accordionRoute = useStore.useAccordionRoute();
    const [validated, setValidated] = useState<boolean|undefined>();
    const translations = useStore.useTranslations();
    const inputRef = useRef<HTMLInputElement>(null);
    const setSelectedAddressRef = useRef(setSelectedAddress);

    useEffect(() => {
        setSelectedAddressRef.current = setSelectedAddress;
    }, [setSelectedAddress]);

    // Attach the Adressevaelger autocomplete widget to the input once on mount.
    useEffect(() => {
        if (!inputRef.current) return;

        const instance = adressevaelger(inputRef.current, {
            token: ADRESSEVAELGER_TOKEN,
            fuzzy: true,
            select: (selected) => {
                const addressText = inputRef.current?.value ?? "";
                setSelectedAddressRef.current(mapSelectionToAddressData(selected, addressText));
                setValidated(true);
            },
        });

        return () => {
            instance?.listElement?.remove?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //A manual edit invalidates the previously selected address until a new suggestion is chosen
    const handleChange = () => {
        setSelectedAddressRef.current(EMPTY_ADDRESS);
        setValidated(inputRef.current?.value ? false : undefined);
    };

    return(
        <Container>
            <Label htmlFor={id}>{labelText}</Label>
            <div className="autocomplete-container">
                <Input 
                    ref={inputRef}
                    id={id}  
                    name={name} 
                    placeholder={placeholder} 
                    autoComplete={autocomplete}
                    validated={validated}
                    onChange={handleChange}
                    />
            </div>

            <HintMessage>F.eks. Vesterbrogade 12, 3. tv, 1620 København V</HintMessage>

            {(validated !== undefined && accordionRoute === step && !validated) && (
                <InputErrorMessage>{translations?.informationAboutYouStep.errorAddress}</InputErrorMessage>
            )}
        </Container>
    )
}